"use client";

import { RestrictToVerticalAxis } from "@dnd-kit/abstract/modifiers";
import { DragDropProvider, DragOverlay } from "@dnd-kit/react";
import { useSortable } from "@dnd-kit/react/sortable";
import type { PropsWithChildren, ReactNode } from "react";
import { cn } from "@/lib/utils";

export type DragHandleProps = {
  ref: (node: HTMLElement | null) => void;
};

export type SortableItemProps = {
  readonly id: string | number;
  readonly index: number;
  readonly group?: string;
  readonly disabled?: boolean;
  readonly children?: ReactNode | ((props: { dragHandleProps: DragHandleProps; isDragging: boolean }) => ReactNode);
  readonly className?: string;
};

export const SortableItem = ({ id, index, group, disabled, children, className }: SortableItemProps) => {
  const { ref, handleRef, isDragging } = useSortable({
    id,
    index,
    group,
    disabled,
    modifiers: [RestrictToVerticalAxis],
  });

  const dragHandleProps: DragHandleProps = { ref: handleRef };

  return (
    <div ref={ref} className={cn(isDragging && "opacity-50", className)} data-dragging={isDragging || undefined}>
      {typeof children === "function" ? children({ dragHandleProps, isDragging }) : children}
    </div>
  );
};

export type SortableListProps = {
  children: ReactNode;
  className?: string;
};

export const SortableList = ({ children, className }: SortableListProps) => (
  <div className={cn("flex flex-col", className)}>{children}</div>
);

// biome-ignore lint/suspicious/noExplicitAny: dnd-kit event types are complex generics
type DragEndHandler = (event: any) => void;

export type SortableProviderProps = PropsWithChildren<{
  onDragEnd: DragEndHandler;
}>;

export const SortableProvider = ({ children, onDragEnd }: SortableProviderProps) => (
  <DragDropProvider onDragEnd={onDragEnd}>{children}</DragDropProvider>
);

export { DragOverlay };
